import { SidebarTrigger } from '../../components/ui/sidebar';
import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePipelineNodes, useBuildTree } from '../../hooks/usePipelineNodes';
import { usePipelineNodeRelationships } from '../../hooks/usePipelineNodeRelationships';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Settings, Plus } from 'lucide-react';
import type { PipelineNode } from '../../services/pipelineNode.service';
import { pipelineNodeService } from '../../services/pipelineNode.service';
import type { PipelineNodeRelationship } from '../../hooks/usePipelineNodeRelationships';
import { NodeTreeView } from '../../components/NodeTreeView';
import { ViewModeToggle } from '../../components/ViewModeToggle';
import type { ViewMode } from '../../components/ViewModeToggle';
import { getPipelineNodeIcon } from '../../components/PipelineNodeIcon';

function formatNumber(num?: number): string {
  if (num === undefined || num === null) return '-';
  return new Intl.NumberFormat('en-US').format(num);
}


function getRecordCount(node: PipelineNode): number | undefined {
  return node.shape?.total_records ?? node.total_records;
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return 'Invalid date';
  }
}

type NodeStatus = 'Error' | 'Needs Build' | 'OK';

function getNodeStatus(node: PipelineNode): NodeStatus {
  if (!node.last_build_completed) {
    return 'Needs Build';
  }

  if (node.build_error) {
    return 'Error';
  }

  if (node.marked_stale_at) {
    const staleAt = new Date(node.marked_stale_at);
    const lastBuild = new Date(node.last_build_completed);

    if (staleAt > lastBuild) {
      return 'Needs Build';
    }
  }

  return 'OK';
}

function getStatusBadge(status: NodeStatus) {
  switch (status) {
    case 'Error':
      return (
        <Badge variant="destructive">
          Error
        </Badge>
      );
    case 'Needs Build':
      return (
        <Badge variant="outline" className="border-warning text-warning">
          Needs Build
        </Badge>
      );
    case 'OK':
      return (
        <Badge variant="secondary" className="bg-success/10 text-success border-success/30">
          OK
        </Badge>
      );
  }
}

interface RelationshipDisplay {
  type: 'parent' | 'child';
  nodeName: string;
  nodeId: string;
  relationshipName: string;
}

export default function Entities() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [newEntityDialogOpen, setNewEntityDialogOpen] = useState(false);
  const [newEntityName, setNewEntityName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const pageSize = 50;

  // Only include DATE_DIMENSION and DIMENSION node types
  const includedNodeTypes = ['DATE_DIMENSION', 'DIMENSION'];

  const { data, isLoading, error } = usePipelineNodes({
    page,
    pageSize,
    filters: { node_type: { $in: includedNodeTypes } },
  });

  const { data: relationships, isLoading: isRelationshipsLoading } = usePipelineNodeRelationships();

  const { data: buildTreeData, isLoading: isTreeLoading, error: treeError } = useBuildTree({
    enabled: viewMode === 'tree',
  });

  const nodes = data?.records || [];

  // Create a map of node IDs to node names for relationship display
  const nodeIdToName = useMemo(() => {
    const map = new Map<string, string>();
    (data?.records || []).forEach((node: PipelineNode) => {
      map.set(node.id, node.label || node.name || 'Unnamed Node');
    });
    return map;
  }, [data?.records]);

  // Create a map of node IDs to their relationships
  const nodeRelationships = useMemo(() => {
    const map = new Map<string, RelationshipDisplay[]>();

    if (!relationships) return map;

    relationships.forEach((rel: PipelineNodeRelationship) => {
      // For the child node, add a "Child of <parent>" relationship
      if (!map.has(rel.child_node_id)) {
        map.set(rel.child_node_id, []);
      }
      map.get(rel.child_node_id)!.push({
        type: 'child',
        nodeName: nodeIdToName.get(rel.parent_node_id) || 'Unknown Node',
        nodeId: rel.parent_node_id,
        relationshipName: rel.name || 'Unnamed Relationship',
      });

      // For the parent node, add a "Parent of <child>" relationship
      if (!map.has(rel.parent_node_id)) {
        map.set(rel.parent_node_id, []);
      }
      map.get(rel.parent_node_id)!.push({
        type: 'parent',
        nodeName: nodeIdToName.get(rel.child_node_id) || 'Unknown Node',
        nodeId: rel.child_node_id,
        relationshipName: rel.name || 'Unnamed Relationship',
      });
    });

    return map;
  }, [relationships, nodeIdToName]);

  // Filter tree nodes to only include DIMENSION and DATE_DIMENSION types
  const filteredTreeNodes = useMemo(() => {
    return buildTreeData?.nodes.filter(
      (node) => includedNodeTypes.includes(node.type)
    ) || [];
  }, [buildTreeData?.nodes]);

  // Filter edges to only include those between visible nodes
  // For NODE_RELATIONSHIP edges, swap from/to to show child -> parent direction.
  // The backend returns parent -> child because the build order requires parents
  // to be built before children. But for the UI, we want to show child -> parent
  // (e.g., Orders -> Products) because the child entity references the parent
  // via a foreign key relationship.
  const filteredTreeEdges = useMemo(() => {
    const visibleNodeIds = new Set(filteredTreeNodes.map(n => n.id));
    return (buildTreeData?.edges || [])
      .filter((edge) => visibleNodeIds.has(edge.from_id) && visibleNodeIds.has(edge.to_id))
      .map(e => e.dependency_type === 'NODE_RELATIONSHIP'
        ? { ...e, from_id: e.to_id, to_id: e.from_id }
        : e
      );
  }, [buildTreeData?.edges, filteredTreeNodes]);

  const totalPages = data?.total_pages || 1;

  const handleConfigure = (nodeId: string) => {
    navigate(`/develop/entities/${nodeId}`);
  };

  const handleTreeNodeClick = (pipelineNodeId: string) => {
    navigate(`/develop/modeling/${pipelineNodeId}`);
  };

  const handleCreateEntity = async () => {
    if (!newEntityName.trim()) return;

    setIsCreating(true);
    try {
      const newNode = await pipelineNodeService.create({
        name: newEntityName.trim(),
        label: newEntityName.trim(),
        node_type: 'DIMENSION',
      });
      queryClient.invalidateQueries({ queryKey: ['pipelineNodes'] });
      queryClient.invalidateQueries({ queryKey: ['buildTree'] });
      setNewEntityDialogOpen(false);
      setNewEntityName('');
      navigate(`/develop/entities/${newNode.id}`);
    } catch (error) {
      console.error('Failed to create entity:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1">
            <SidebarTrigger className="md:hidden h-5 w-5" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/develop">Develop</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Entities</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          {/* Page Title */}
          <h1 className="text-xl font-semibold text-foreground">Entities</h1>
        </div>
        {/* View Toggle and New Entity Button */}
        <div className="flex items-center gap-3">
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          <Button
            onClick={() => setNewEntityDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            New Entity
          </Button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'table' ? (
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
          {isLoading || isRelationshipsLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading entities...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load entities. Please try again.
            </p>
          </div>
        ) : nodes.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No entities found.</p>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Last Build Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Relationships</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nodes.map((node: PipelineNode) => {
                    const rels = nodeRelationships.get(node.id) || [];
                    return (
                      <TableRow key={node.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {getPipelineNodeIcon(node.node_type)}
                            <div className="flex flex-col">
                              <span>{node.label || node.name || 'Unnamed Node'}</span>
                              {node.name && node.name !== node.label && (
                                <span className="text-xs text-muted-foreground font-normal">
                                  {node.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatNumber(getRecordCount(node))}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(node.last_build_completed)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(getNodeStatus(node))}
                        </TableCell>
                        <TableCell>
                          {rels.length === 0 ? (
                            <span className="text-muted-foreground text-sm">None</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {rels.map((rel, idx) => (
                                <span key={idx} className="text-sm">
                                  {rel.type === 'child' ? 'Child of' : 'Parent of'}{' '}
                                  <Link
                                    to={`/develop/entities/${rel.nodeId}`}
                                    className="text-primary hover:underline"
                                  >
                                    {rel.nodeName}
                                  </Link>
                                  {' '}
                                  <span className="text-muted-foreground">
                                    ({rel.relationshipName})
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleConfigure(node.id)}
                            title="Configure"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) setPage(page - 1);
                        }}
                        style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                      />
                    </PaginationItem>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(pageNum);
                              }}
                              isActive={pageNum === page}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={`ellipsis-${pageNum}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}

                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) setPage(page + 1);
                        }}
                        style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          {isTreeLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Loading tree...</p>
              </div>
            </div>
          ) : treeError ? (
            <div className="p-8">
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-destructive text-sm">
                  Failed to load tree view. Please try again.
                </p>
              </div>
            </div>
          ) : filteredTreeNodes.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">No entities found.</p>
            </div>
          ) : (
            <NodeTreeView
              nodes={filteredTreeNodes}
              edges={filteredTreeEdges}
              onNodeClick={handleTreeNodeClick}
              className="h-full"
            />
          )}
        </div>
      )}

      {/* New Entity Dialog */}
      <Dialog open={newEntityDialogOpen} onOpenChange={setNewEntityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Entity</DialogTitle>
            <DialogDescription>
              Create a new entity. You can configure its details after creation.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="entity-name">Entity Name</Label>
              <Input
                id="entity-name"
                value={newEntityName}
                onChange={(e) => setNewEntityName(e.target.value)}
                placeholder="Enter entity name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newEntityName.trim()) {
                    handleCreateEntity();
                  }
                }}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewEntityDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateEntity}
              disabled={!newEntityName.trim() || isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Entity'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
